import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router';

/**
 * Lets a screen holding unsaved work veto navigation started elsewhere.
 *
 * The URL page already guards the exits it can see — its own breadcrumb, the
 * hostname link, the Back button, and `beforeunload`. It cannot see the header
 * nav or the ⌘K palette, which live in the shell, so those would leave the page
 * silently and discard a weight draft.
 *
 * That is worse than having no guard at all: an operator who has been asked
 * "discard these percentages?" twice learns that the console will stop them,
 * and then loses a split to a nav click that does not ask.
 *
 * Deliberately not solved with React Router's `useBlocker`, which requires a
 * data router (`createBrowserRouter`) while this app mounts `BrowserRouter` +
 * `Routes`. Migrating the router to close this gap would be a far larger change
 * than the gap warrants.
 */
export type NavigationVeto = (to: string) => boolean;

type GuardContextValue = {
  register: (veto: NavigationVeto | null) => void;
  navigateGuarded: (to: string) => void;
};

const GuardContext = createContext<GuardContextValue | null>(null);

export function NavigationGuardProvider({ children }: { children: ReactNode }) {
  // A ref rather than state: registering a veto must not re-render the shell,
  // and the veto is only read at the moment navigation is attempted.
  const veto = useRef<NavigationVeto | null>(null);
  const navigate = useNavigate();

  const register = useCallback((next: NavigationVeto | null) => {
    veto.current = next;
  }, []);

  const navigateGuarded = useCallback(
    (to: string) => {
      // `true` means the veto has taken responsibility: it will show its own
      // confirmation and navigate itself if the operator confirms. `false`
      // means "not my concern".
      if (veto.current?.(to) === true) return;
      void navigate(to);
    },
    [navigate],
  );

  const value = useMemo<GuardContextValue>(
    () => ({ register, navigateGuarded }),
    [register, navigateGuarded],
  );

  return <GuardContext.Provider value={value}>{children}</GuardContext.Provider>;
}

/**
 * Navigate, honouring any registered veto.
 *
 * Falls back to plain navigation when no provider is mounted, so a screen
 * rendered alone in a test needs no extra wrapper.
 */
export function useGuardedNavigate(): (to: string) => void {
  const context = useContext(GuardContext);
  const navigate = useNavigate();

  return useCallback(
    (to: string) => {
      if (context !== null) context.navigateGuarded(to);
      else void navigate(to);
    },
    [context, navigate],
  );
}

/** Registers this screen's veto for as long as it is mounted. */
export function useNavigationVeto(veto: NavigationVeto | null): void {
  const context = useContext(GuardContext);

  // Held in a ref so a veto that changes every render does not re-register,
  // while the registered callback still sees the current one.
  const latest = useRef(veto);
  latest.current = veto;

  useEffect(() => {
    const register = context?.register;
    if (register === undefined) return undefined;

    register((to) => latest.current?.(to) ?? false);
    // Unregistering on unmount matters: a stale veto would block navigation
    // away from a screen that is no longer on the page.
    return () => register(null);
  }, [context]);
}
